import { useMemo, useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export function useMembership() {
  const { membershipType, userProfile, profileLoading } = useAuth()
  const [membershipLimits, setMembershipLimits] = useState<any>(null)
  const [limitsLoading, setLimitsLoading] = useState(true)

  const isPro = useMemo(() => membershipType === 'pro', [membershipType])
  const isFree = useMemo(() => membershipType === 'free', [membershipType])
  const isPublisher = useMemo(() => membershipType === 'publisher', [membershipType])

  // Fetch membership limits
  useEffect(() => {
    if (!membershipType) {
      setLimitsLoading(false)
      return
    }

    const fetchLimits = async () => {
      try {
        const { data, error } = await supabase
          .from('membership_limits')
          .select('*')
          .eq('membership_type', membershipType)
          .maybeSingle()

        if (error) throw error
        setMembershipLimits(data)
      } catch (err) {
        console.error('Failed to fetch membership limits:', err)
        setMembershipLimits(null)
      } finally {
        setLimitsLoading(false)
      }
    }

    fetchLimits()
  }, [membershipType])

  const canPublishTrades = useMemo(() => {
    return membershipLimits?.can_publish_trades ?? false
  }, [membershipLimits])

  const canCreatePools = useMemo(() => {
    return membershipLimits?.can_create_pools ?? false
  }, [membershipLimits])

  const canFollowUsers = useMemo(() => {
    return membershipLimits?.can_follow_users ?? false
  }, [membershipLimits])

  const canUseSocialTrade = useMemo(() => {
    // Social Trade module (as a trader) is driven by membership limits,
    // not by hard-coded plan names.
    return canPublishTrades || canCreatePools
  }, [canPublishTrades, canCreatePools])

  const canCreateStrategyVersions = useMemo(() => {
    return membershipLimits?.can_create_strategy_versions ?? false
  }, [membershipLimits])

  const canUseAccounts = useMemo(() => {
    return membershipLimits?.can_use_accounts ?? false
  }, [membershipLimits])

  /** Max active strategies for this membership (null = unlimited). From admin panel membership_limits.max_strategies. */
  const maxStrategies = useMemo(() => {
    const n = membershipLimits?.max_strategies
    return typeof n === 'number' ? n : null
  }, [membershipLimits])

  /** Max accounts for this membership (null = unlimited). From admin panel membership_limits.max_accounts. */
  const maxAccounts = useMemo(() => {
    const n = membershipLimits?.max_accounts
    return typeof n === 'number' ? n : null
  }, [membershipLimits])

  return {
    membershipType,
    userProfile,
    profileLoading,
    isPro,
    isFree,
    isPublisher,
    canPublishTrades,
    canCreatePools,
    canFollowUsers,
    canUseSocialTrade,
    canCreateStrategyVersions,
    canUseAccounts,
    maxStrategies,
    maxAccounts,
    membershipLimits,
    limitsLoading,
  }
}

